import styled from '@emotion/styled';
import {useBreakpointDown} from '@travel-frontend/core';
import {useSwipeableImages} from '@travel-frontend/ui-components';
import {useCallback} from 'react';

import {BestOfferBadge} from '@/components/BestOfferBadge';
import {Container} from '@/components/HotelBoxGallery/components/Container';
import {PositionableNonBlocking} from '@/components/HotelBoxGallery/components/PositionableNonBlocking';
import {ImageCarousel} from '@/components/ImageCarousel';
import {config} from '@/config';
import {isHighRatedWithManyReviews} from '@/utils/isHiglyRatedAndReviwed';

type Props = {
  images: Array<string>;
  fallbackImage?: React.ReactNode;
  reviewCount?: number;
  ratingScore?: number;
  interceptorElement?: React.ReactNode;
  onClickImage?: () => void;
  imageLoadingStrategy?: 'eager' | 'lazy';
  small?: boolean;
};

const GalleryComponent: React.FunctionComponent<Props> = ({
  images,
  fallbackImage,
  reviewCount,
  ratingScore,
  interceptorElement,
  onClickImage,
  imageLoadingStrategy = 'eager',
  small = false,
  ...restProps
}) => {
  const {goBack, goForward, getInternalProps} = useSwipeableImages({
    imageCount: images.length,
    imagesPerSlide: 1,
  });

  const {bestOfferBadgeEnabled} = config.hotelBox;

  const handleClickPrevious: React.MouseEventHandler<HTMLElement> = useCallback(
    (event) => {
      // Prevent native link behaviour when `HotelBoxTile` is rendered as an `a`
      event.preventDefault();
      event.stopPropagation();
      goBack();
    },
    [goBack]
  );

  const handleClickNext: React.MouseEventHandler<HTMLElement> = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      goForward();
    },
    [goForward]
  );

  const carousel =
    images.length > 0 ? (
      <ImageCarousel
        {...getInternalProps()}
        imageLoadingStrategy={imageLoadingStrategy}
        images={images}
        hasMouseEvents={false}
        onClick={onClickImage}
        onClickPrev={handleClickPrevious}
        onClickNext={handleClickNext}
      />
    ) : (
      fallbackImage
    );

  const canShowBadge = isHighRatedWithManyReviews(ratingScore, reviewCount);

  const smDown = useBreakpointDown('sm');
  return (
    <Container isCompact={smDown || small} {...restProps}>
      {carousel}
      {interceptorElement}
      {bestOfferBadgeEnabled && canShowBadge && (
        <PositionableNonBlocking position="bottom">
          <BestOfferBadge />
        </PositionableNonBlocking>
      )}
    </Container>
  );
};

export const HotelBoxGallery = styled(GalleryComponent)``;
